import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 同步从 localStorage 读取 token 和用户信息，避免首次渲染闪现 login 页面
const getInitialAuthState = () => {
  const storedToken = localStorage.getItem('token');
  if (!storedToken) {
    return { token: null, user: null, loading: false };
  }
  try {
    const decoded = jwtDecode(storedToken);
    if (decoded.exp * 1000 > Date.now()) {
      return {
        token: storedToken,
        user: {
          id: decoded.user_id || decoded.id,
          username: decoded.username || decoded.sub,
          is_admin: decoded.is_admin || false,
          exp: decoded.exp,
        },
        loading: false,
      };
    }
  } catch (e) {
    // token 无效，清除
    localStorage.removeItem('token');
  }
  return { token: null, user: null, loading: false };
};

export const AuthProvider = ({ children }) => {
  const initialAuth = getInitialAuthState();
  const [user, setUser] = useState(initialAuth.user);
  const [token, setToken] = useState(initialAuth.token);
  const [loading, setLoading] = useState(initialAuth.loading);

  const fetchProfile = async (jwtToken) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data;
    } catch (err) {
      console.error('Fetch profile failed:', err);
      return null;
    }
  };

  // 检查本地存储中的token
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const decodedToken = jwtDecode(storedToken);
        
        // 检查token是否过期
        if (decodedToken.exp * 1000 > Date.now()) {
          setToken(storedToken);
          fetchProfile(storedToken).then((profile) => {
            setUser({
              id: decodedToken.user_id || decodedToken.id,
              username: decodedToken.username || decodedToken.sub,
              is_admin: decodedToken.is_admin || false,
              exp: decodedToken.exp,
              avatar: profile?.avatar,
              invite_code: profile?.invite_code,
              vip_expires_at: profile?.vip_expires_at,
            });
          });
        } else {
          // Token过期，清除
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // 登录函数
  const login = async (username, password) => {
    try {
      // 调用后端认证API
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const rawBody = await response.text();
        let parsedBody = null;

        if (contentType.includes('application/json') && rawBody) {
          try {
            parsedBody = JSON.parse(rawBody);
          } catch (parseError) {
            console.error('Failed to parse error response as JSON:', parseError);
          }
        }

        const detailMessage =
          (parsedBody && (parsedBody.detail || parsedBody.message)) ||
          rawBody ||
          `登录失败，状态码 ${response.status}`;

        const error = new Error(detailMessage);
        error.debugInfo = {
          status: response.status,
          statusText: response.statusText,
          body: parsedBody || rawBody || null,
          url: `${API_BASE_URL}/api/auth/login`
        };
        throw error;
      }

      const data = await response.json();
      const { access_token: newToken } = data;

      // 解码token获取用户信息
      const decodedToken = jwtDecode(newToken);
      
      // 保存token到localStorage
      localStorage.setItem('token', newToken);
      
      // 更新状态
      setToken(newToken);
      const profile = await fetchProfile(newToken);
      setUser({
        id: decodedToken.user_id || decodedToken.id,
        username: decodedToken.username || decodedToken.sub,
        is_admin: decodedToken.is_admin || false,
        exp: decodedToken.exp,
        avatar: profile?.avatar,
        invite_code: profile?.invite_code,
        vip_expires_at: profile?.vip_expires_at,
      });

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || '登录失败，请检查用户名和密码',
        debugInfo: error.debugInfo || {
          name: error.name,
          message: error.message,
          stack: error.stack
        }
      };
    }
  };

  // 短信验证码登录
  const loginWithSms = async (phone, code) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, code }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const rawBody = await response.text();
        let parsedBody = null;

        if (contentType.includes('application/json') && rawBody) {
          try {
            parsedBody = JSON.parse(rawBody);
          } catch (parseError) {
            console.error('Failed to parse error response as JSON:', parseError);
          }
        }

        const detailMessage =
          (parsedBody && (parsedBody.detail || parsedBody.message)) ||
          rawBody ||
          `登录失败，状态码 ${response.status}`;

        const error = new Error(detailMessage);
        error.debugInfo = {
          status: response.status,
          statusText: response.statusText,
          body: parsedBody || rawBody || null,
          url: `${API_BASE_URL}/api/auth/login-sms`
        };
        throw error;
      }

      const data = await response.json();
      const { access_token: newToken } = data;

      const decodedToken = jwtDecode(newToken);
      localStorage.setItem('token', newToken);
      setToken(newToken);
      const profile = await fetchProfile(newToken);
      setUser({
        id: decodedToken.user_id || decodedToken.id,
        username: decodedToken.username || decodedToken.sub,
        is_admin: decodedToken.is_admin || false,
        exp: decodedToken.exp,
        avatar: profile?.avatar,
        invite_code: profile?.invite_code,
        vip_expires_at: profile?.vip_expires_at,
      });

      return { success: true };
    } catch (error) {
      console.error('SMS Login error:', error);
      return {
        success: false,
        message: error.message || '验证码登录失败，请重试',
        debugInfo: error.debugInfo || {
          name: error.name,
          message: error.message,
          stack: error.stack
        }
      };
    }
  };

  // 注册函数
  const register = async ({ phone, smsCode, username, password, invite }) => {
    try {
      if (!phone || !smsCode) {
        throw new Error('请先完成手机验证码验证');
      }
      // 调用后端注册API
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          sms_code: smsCode,
          username,
          password,
          invite_code: invite,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '注册失败');
      }

      const data = await response.json();
      return { success: true, message: data.message, inviteCode: data.invite_code };
    } catch (error) {
      console.error('Register error:', error);
      return { 
        success: false, 
        message: error.message || '注册失败，请重试' 
      };
    }
  };

  // 退出登录
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // 检查token是否即将过期
  const isTokenExpiringSoon = () => {
    if (!user || !user.exp) return false;
    const timeUntilExpiry = user.exp * 1000 - Date.now();
    return timeUntilExpiry < 5 * 60 * 1000; // 5分钟内过期
  };

  // 刷新token
  const refreshToken = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const newToken = data.token;
        
        localStorage.setItem('token', newToken);
        setToken(newToken);
        
        const decodedToken = jwtDecode(newToken);
        setUser({
          id: decodedToken.user_id || decodedToken.id,
          username: decodedToken.username || decodedToken.sub,
          is_admin: decodedToken.is_admin || false,
          exp: decodedToken.exp
        });
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  // 创建模拟token（开发阶段使用）
  const createMockToken = (username) => {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    
    // 检查是否为管理员用户（这里可以根据用户名判断，或者从数据库查询）
    const isAdmin = username === 'admin' || username === 'demo' || username.includes('admin');
    
    const payload = btoa(JSON.stringify({
      user_id: 1,
      username: username,
      is_admin: isAdmin,
      exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60), // 24小时后过期
      iat: Math.floor(Date.now() / 1000)
    }));
    const signature = 'mock-signature';
    
    return `${header}.${payload}.${signature}`;
  };

  /** 局部更新 user 对象（例如修改用户名后刷新前端状态） */
  const updateUser = (patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  /** 是否为有效 VIP */
  const isVip = useMemo(() => {
    if (!user?.vip_expires_at) return false;
    return new Date(user.vip_expires_at) > new Date();
  }, [user?.vip_expires_at]);

  const value = {
    user,
    token,
    loading,
    login,
    loginWithSms,
    register,
    logout,
    updateUser,
    isTokenExpiringSoon,
    refreshToken,
    isAuthenticated: !!token && !!user,
    isVip,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
